/**
 * Generic dirty-form guard.
 *
 * Attaches unsaved-change detection to every form that carries the
 * `data-dirty-form-guard` attribute.  Any element with the attribute
 * `data-dirty-form-cancel` inside the form acts as a clean-exit signal
 * (e.g. a Cancel link) and suppresses the browser prompt on click.
 *
 * Included in the global bundle (index.ts) and is a safe no-op on pages
 * that have no guarded forms.
 *
 * @returns A cleanup function that removes the window `beforeunload`
 *   listener (used in tests to avoid listener accumulation across
 *   module re-evaluations).
 */
export function initDirtyFormGuard(): (() => void) | undefined {
    const forms = Array.from(
        document.querySelectorAll<HTMLFormElement>('[data-dirty-form-guard]'),
    );

    if (forms.length === 0) return undefined;

    const dirtySet = new WeakSet<HTMLFormElement>();

    for (const form of forms) {
        form.addEventListener('input', () => { dirtySet.add(form); });
        form.addEventListener('change', () => { dirtySet.add(form); });
        form.addEventListener('submit', () => { dirtySet.delete(form); });

        const cancelEls = form.querySelectorAll('[data-dirty-form-cancel]');
        for (const el of cancelEls) {
            el.addEventListener('click', () => { dirtySet.delete(form); });
        }
    }

    function onBeforeUnload(e: BeforeUnloadEvent): void {
        const anyDirty = forms.some(f => dirtySet.has(f));
        if (!anyDirty) return;
        // Modern browsers show a generic confirmation prompt regardless of returnValue text.
        // Setting returnValue to '' (or any value) triggers the dialog; custom text is ignored.
        e.preventDefault();
        e.returnValue = '';
    }

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => { window.removeEventListener('beforeunload', onBeforeUnload); };
}

// Auto-initialise when loaded as part of the bundle.
initDirtyFormGuard();
